/**
 * Revenue agent — the second producer that does *real* work, not simulation.
 *
 * Where `followUpAgent` protects the top of the funnel (drifting prospects), this
 * one protects the bottom: customers whose money is about to slip. It reads the
 * *real* customer data, finds the revenue events that need a human touch — a
 * past-due invoice, a payment landing soon, or a contract coming up for renewal —
 * and proposes a *real* `create_task` write for each one into the approvals inbox.
 *
 * It performs no write itself — it only proposes. The operator approves in the
 * Dashboard approvals inbox, and `resolveApproval` runs the action through
 * `runActionWithUndo`, which genuinely creates the task (tied to the customer so
 * it shows on their page) and is undoable. So the loop is honest end-to-end:
 *
 *   real data  →  real proposal  →  operator approval  →  real store write
 *
 * Domains are split on purpose so two deployed agents never double up: the
 * outreach monitor owns follow-ups (`schedule_follow_up`), this owns revenue
 * (`create_task`). No network, no API key — same db adapter the humans use, so
 * it works on localStorage today and Firestore the moment the flag flips.
 */

import { scanSystem } from './systemScanner';
import { queueApproval, listApprovals } from './approvals';
import type { Person } from '../db/types';
import type { CreateTaskAction } from './types';

/** Scanner finding categories this agent knows how to turn into a task. */
const ACTIONABLE_CATEGORIES = new Set([
  'Billing past due',
  'Billing due soon',
  'Renewal approaching',
]);

const DAY = 24 * 60 * 60 * 1000;

/**
 * How many days out a soft (not-yet-due) revenue event must be to be worth a
 * task today. The scanner currently surfaces *every* future billing date and
 * renewal regardless of distance, so the producer applies its own near-term
 * window — it would be noise to open a task for a renewal six months away.
 * Past-due billing has no window (it's already late → always actionable).
 */
const HORIZON_DAYS: Record<string, number> = {
  'Billing due soon': 14,
  'Renewal approaching': 45,
};

/**
 * The relevant date for a soft revenue finding, or undefined when it can't be
 * resolved. Past-due findings don't go through here (they have no horizon).
 */
function softDate(category: string, person: Person, findingTitle: string): string | undefined {
  if (category === 'Renewal approaching') return person.renewalDate;
  const prefix = `${person.name} · `;
  const label = findingTitle.startsWith(prefix) ? findingTitle.slice(prefix.length) : '';
  return label ? person.billingDates?.find((b) => b.label === label)?.date : undefined;
}

/**
 * True when a soft revenue finding is close enough to warrant a task now.
 * Always true for past-due billing. For due-soon / renewals, the event's real
 * date must fall between today and the category's horizon.
 */
function withinHorizon(category: string, person: Person, findingTitle: string, now: number): boolean {
  if (category === 'Billing past due') return true;
  const horizon = HORIZON_DAYS[category];
  if (horizon == null) return false;
  const dateStr = softDate(category, person, findingTitle);
  const ms = dateStr ? Date.parse(dateStr) : NaN;
  if (Number.isNaN(ms)) return false;
  const daysOut = (ms - now) / DAY;
  return daysOut >= 0 && daysOut <= horizon;
}

export interface ProposeRevenueTasksResult {
  /** How many new revenue tasks were queued for approval. */
  proposed: number;
  /** Findings that already had a matching pending task (skipped). */
  skipped: number;
}

/**
 * Turn one revenue finding into a concrete, executable `create_task` proposal.
 * `null` when the finding can't be mapped to a real customer (defensive — the
 * scanner only emits these for known customers, so this is belt-and-braces).
 *
 * Each task carries the real at-risk dollar figure in `value` so the value
 * ledger can credit the dollars an approved revenue action touched: the
 * invoice amount for a billing task, the account value (ARR) for a renewal.
 * Influenced / at-risk, never banked — the task chases that money, it doesn't
 * collect it. Omitted (left `undefined`) when the underlying record carries no
 * amount, so the ledger never invents a number.
 *
 * Exported for unit testing the value derivation without touching the store.
 */
export function taskFor(category: string, person: Person, findingTitle: string): CreateTaskAction | null {
  const base = { kind: 'create_task' as const, personId: person.id, personName: person.name };

  if (category === 'Renewal approaching') {
    return {
      ...base,
      title: `Start renewal — ${person.name}`,
      priority: 'high',
      dueDate: person.renewalDate ?? null,
      // The ARR coming up for renewal is the dollars this task protects.
      value: person.accountValue,
    };
  }

  // Billing past due / due soon: recover the milestone label from the finding
  // title (`${name} · ${label}`) so the proposed task names the exact invoice,
  // and pull its real date for the task's due date.
  const prefix = `${person.name} · `;
  const label = findingTitle.startsWith(prefix) ? findingTitle.slice(prefix.length) : '';
  const milestone = label ? person.billingDates?.find((b) => b.label === label) : undefined;
  const pastDue = category === 'Billing past due';

  return {
    ...base,
    title: `${pastDue ? 'Collect past-due payment' : 'Confirm upcoming payment'} — ${person.name}${
      label ? ` · ${label}` : ''
    }`,
    priority: pastDue ? 'urgent' : 'high',
    dueDate: milestone?.date ?? null,
    // The exact invoice amount this task chases — the dollars at risk.
    value: milestone?.amount,
  };
}

/**
 * Scan the customers in `people` and queue a `create_task` approval for each
 * pressing revenue event. De-dupes by task title against pending approvals so
 * re-running the agent doesn't pile duplicate tasks for the same invoice or
 * renewal. Returns how many were proposed vs. skipped.
 */
export async function proposeRevenueTasks(
  people: Person[],
  now = Date.now(),
): Promise<ProposeRevenueTasksResult> {
  // Only the customer buckets need data; the other collections aren't consulted
  // for revenue findings, so empty arrays keep the scan focused.
  const { findings } = scanSystem({ tickets: [], tasks: [], people, accounts: [], now });
  const byId = new Map(people.map((p) => [p.id, p]));
  const revenue = findings.filter((f) => {
    if (f.source !== 'account' || !ACTIONABLE_CATEGORIES.has(f.category)) return false;
    const person = byId.get(f.entityId);
    // Drop soft findings that are too far out to act on today (the scanner
    // over-flags future dates — see HORIZON_DAYS).
    return person ? withinHorizon(f.category, person, f.title, now) : false;
  });

  // Don't propose a second task for an invoice/renewal that already has one
  // waiting in the inbox. Titles are deterministic per (customer, milestone),
  // so matching on title is a stable de-dupe key.
  const pending = await listApprovals('pending');
  const alreadyQueued = new Set(
    pending
      .filter((a) => a.action.kind === 'create_task')
      .map((a) => (a.action as CreateTaskAction).title),
  );

  let proposed = 0;
  let skipped = 0;
  for (const f of revenue) {
    const person = byId.get(f.entityId);
    if (!person) {
      skipped++;
      continue;
    }
    const action = taskFor(f.category, person, f.title);
    if (!action || alreadyQueued.has(action.title)) {
      skipped++;
      continue;
    }
    await queueApproval(action, 'scheduler', f.detail);
    // Guard against duplicate findings producing the same task within one run.
    alreadyQueued.add(action.title);
    proposed++;
  }

  return { proposed, skipped };
}
